import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { NavLink, Link } from "react-router-dom";
import { HiOutlineMenu, HiOutlineX, HiOutlineChevronDown } from "react-icons/hi";
import { GlassButton } from "../styles/GlassComponents";
import theme from "../styles/theme";

const HeaderWrap = styled.header`
  position: sticky; top: 0; z-index: 1200;
  background: #0a0f1c;
  border-bottom: none;
  box-shadow: 
    0 0 20px rgba(255, 0, 128, 0.3),
    0 -10px 30px rgba(255, 0, 128, 0.2),
    0 -20px 40px rgba(255, 0, 128, 0.1);
  transition: all .25s ease;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -30px;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(255, 0, 128, 0.4), rgba(255, 0, 128, 0.2), transparent);
    pointer-events: none;
    filter: blur(1px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(to top, rgba(255, 0, 128, 0.6), transparent);
    pointer-events: none;
  }
`;

const Bar = styled.div`
  height: 88px; display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 0 16px; max-width: 1200px; margin: 0 auto;
  
  @media (max-width: 768px) {
    height: 70px;
    padding: 0 12px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    height: 60px;
    padding: 0 8px;
    gap: 6px;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const neonPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(0, 204, 255, 0.45);
  }
  50% { 
    box-shadow: 0 0 30px rgba(0, 204, 255, 0.6), 0 0 40px rgba(0, 204, 255, 0.3);
  }
`;

const Brand = styled(Link)`
  display: inline-flex; align-items: center; gap: 12px; font-weight: 800; letter-spacing: .2px;
  color: #ffffff;
  transition: transform 0.2s ease;
  font-family: ${({theme})=>theme.typography.fontFamily.heading};
  margin-left: -20px;
  
  &:hover {
    transform: scale(1.02);
  }
  
  img { 
    height: 122px; 
    width: auto; 
    object-fit: contain; 
    border-radius: 8px; 
    filter: drop-shadow(0 0 8px rgba(0, 204, 255, 0.6));
    
    @media (max-width: 768px) {
      height: 80px;
    }
    
    @media (max-width: 480px) {
      height: 60px;
    }
  }
  
  span { 
    font-size: 41px; 
    font-family: 'Fredoka', 'Arial', sans-serif;
    font-weight: bold;
    
    @media (max-width: 768px) {
      font-size: 29px;
    }
    
    @media (max-width: 480px) {
      font-size: 22px;
    }
    
    .inflatable {
      color: #00ccff;
      text-shadow: 0 0 8px #00ccff, 0 0 16px #00ccff;
      
      @media (max-width: 768px) {
        text-shadow: 0 0 5px #00ccff, 0 0 10px #00ccff;
      }
      
      @media (max-width: 480px) {
        text-shadow: 0 0 3px #00ccff, 0 0 6px #00ccff;
      }
    }
    
    .tx {
      color: #ff0080;
      text-shadow: 0 0 8px #ff0080, 0 0 16px #ff0080;
      
      @media (max-width: 768px) {
        text-shadow: 0 0 5px #ff0080, 0 0 10px #ff0080;
      }
      
      @media (max-width: 480px) {
        text-shadow: 0 0 3px #ff0080, 0 0 6px #ff0080;
      }
    }
  }
`;

const Nav = styled.nav`
  display: flex; align-items: center; gap: 18px;
  
  a { 
    color: #00ccff; 
    font-weight: 600; 
    padding: 12px 16px;
    border-radius: 8px;
    transition: all 0.2s ease;
    position: relative;
    text-shadow: 0 0 5px #00ccff;
    background: rgba(0, 204, 255, 0.1);
    border: 1px solid rgba(0, 204, 255, 0.3);
    box-shadow: 0 0 10px rgba(0, 204, 255, 0.3);
    min-height: 44px;
    display: flex;
    align-items: center;
    
    &:hover {
      color: #00e6e6;
      text-shadow: 0 0 8px #00e6e6, 0 0 16px #00e6e6;
      background: rgba(0, 204, 255, 0.2);
      border-color: rgba(0, 204, 255, 0.6);
      box-shadow: 0 0 15px rgba(0, 204, 255, 0.5);
      transform: translateY(-1px);
      animation: ${pulse} 0.6s ease-in-out;
    }
    
    &:active {
      transform: translateY(0) scale(0.98);
    }
  }
  
  a.active { 
    color: #00e6e6; 
    text-shadow: 0 0 8px #00e6e6, 0 0 16px #00e6e6;
    background: rgba(0, 204, 255, 0.2);
    border-color: rgba(0, 204, 255, 0.6);
    box-shadow: 0 0 15px rgba(0, 204, 255, 0.5);
    font-weight: 700;
  }
  
  @media (max-width: 768px) { display: none; }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  display: flex; align-items: center; gap: 4px;
  color: #00ccff; 
  font-weight: 600; 
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(0, 204, 255, 0.3);
  background: rgba(0, 204, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  text-shadow: 0 0 5px #00ccff;
  box-shadow: 0 0 10px rgba(0, 204, 255, 0.3);
  min-height: 44px;
  
  &:hover {
    color: #00e6e6;
    text-shadow: 0 0 8px #00e6e6, 0 0 16px #00e6e6;
    background: rgba(0, 204, 255, 0.2);
    border-color: rgba(0, 204, 255, 0.6);
    box-shadow: 0 0 15px rgba(0, 204, 255, 0.5);
    transform: translateY(-1px);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: #11162b;
  border: 1px solid rgba(0, 204, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(0, 204, 255, 0.4);
  padding: 8px;
  min-width: 200px;
  z-index: 1000;
  opacity: ${({$open}) => $open ? 1 : 0};
  visibility: ${({$open}) => $open ? 'visible' : 'hidden'};
  transform: translateY(${({$open}) => $open ? '0' : '-10px'});
  transition: all 0.2s ease;
  
  a {
    display: block;
    padding: 12px 16px;
    color: #00ccff;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;
    text-shadow: 0 0 5px rgba(0, 204, 255, 0.5);
    
    &:hover {
      background: rgba(0, 204, 255, 0.1);
      color: #00e6e6;
      text-shadow: 0 0 8px rgba(0, 230, 230, 0.8);
      transform: translateX(4px);
      border: 1px solid rgba(0, 204, 255, 0.3);
    }
  }
`;

const Right = styled.div`
  display: flex; align-items: center; gap: 12px;
`;

const BookNowButton = styled.button`
  background: #0a0f1c;
  color: #00ccff;
  border: 2px solid #00ccff;
  padding: 12px 32px;
  border-radius: 25px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
  animation: ${neonPulse} 2s ease-in-out infinite;
  font-family: 'Fredoka', 'Arial', sans-serif;
  text-shadow: 0 0 5px #00ccff;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 8px 20px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 16px;
    font-size: 12px;
  }
  
  &:hover {
    background: #00ccff;
    color: #0a0f1c;
    box-shadow: 0 0 30px rgba(0, 204, 255, 0.6), 0 0 60px rgba(0, 204, 255, 0.3);
    transform: translateY(-2px);
    text-shadow: none;
    animation: none;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Burger = styled.button`
  display: none; 
  border: 0; 
  background: transparent; 
  cursor: pointer;
  color: #00ccff;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  text-shadow: 0 0 5px #00ccff;
  box-shadow: 0 0 10px rgba(0, 204, 255, 0.3);
  
  &:hover {
    background: rgba(0, 204, 255, 0.1);
    color: #00e6e6;
    text-shadow: 0 0 8px #00e6e6, 0 0 16px #00e6e6;
    box-shadow: 0 0 15px rgba(0, 204, 255, 0.5);
  }
  
  @media (max-width: 768px) { 
    display: inline-flex; 
  }
`;

const MobileSheet = styled.div`
  position: fixed; inset: 0; z-index: 1300; display: grid; grid-template-rows: auto 1fr;
  background: rgba(255,255,255,0.96); backdrop-filter: blur(8px);
`;

const MobileTop = styled.div`
  height: 64px; display: flex; align-items: center; justify-content: space-between;
  padding: 0 16px; border-bottom: 1px solid rgba(0,0,0,0.06);
  
  @media (max-width: 480px) {
    height: 56px;
    padding: 0 12px;
  }
`;

const MobileLinks = styled.div`
  display: grid; gap: 8px; padding: 16px;
  
  a { 
    padding: 14px 10px; 
    border-radius: 12px; 
    font-weight: 700; 
    color: ${({theme})=>theme.colors.text.primary};
    transition: all 0.2s ease;
    
    &:hover {
      background: ${({theme})=>theme.colors.blue[100]};
      transform: translateX(4px);
    }
    
    &:active { 
      background: ${({theme})=>theme.colors.blue[200]};
      transform: translateX(4px) scale(0.98);
    }
  }
`;

export default function HeaderSlim(){
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(()=>{
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  },[]);

  return (
    <HeaderWrap $scrolled={scrolled}>
      <Bar>
        <Brand to="/" $scrolled={scrolled}>
          <img src="/inflatabletxlogo.svg" alt="InflatableTx" />
          <span>
            <span className="inflatable">Inflatable</span>
            <span className="tx">TX</span>
          </span>
        </Brand>

        <Nav $scrolled={scrolled}>
          <NavLink to="/" end>Home</NavLink>
          <DropdownContainer>
            <DropdownButton 
              $scrolled={scrolled}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
              aria-label="Inflatables menu"
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              Inflatables <HiOutlineChevronDown size={16} aria-hidden="true" />
            </DropdownButton>
            <DropdownMenu 
              $open={dropdownOpen}
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <Link to="/products">All Inflatables</Link>
              <Link to="/bounce-houses">Bounce Houses</Link>
              <Link to="/water-slides">Water Slides</Link>
              <Link to="/combos">Combos</Link>
              <Link to="/obstacle-courses">Obstacle Courses</Link>
              <Link to="/event-rentals">Event Rentals</Link>
            </DropdownMenu>
          </DropdownContainer>
          <NavLink to="/booking">Booking</NavLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/contact">Contact</NavLink>
        </Nav>

        <Right>
          <Link to="/booking">
            <BookNowButton>
              Book Now
            </BookNowButton>
          </Link>
          <Burger aria-label="Open menu" onClick={()=>setOpen(true)}>
            <HiOutlineMenu size={28} />
          </Burger>
        </Right>
      </Bar>

      {open && (
        <MobileSheet role="dialog" aria-label="Menu">
          <MobileTop>
            <Brand to="/" onClick={()=>setOpen(false)}>
              <img src="/inflatabletxlogo.svg" alt="InflatableTx" style={{
                height: window.innerWidth <= 480 ? '54px' : '72px', 
                filter: 'drop-shadow(0 0 8px rgba(0, 204, 255, 0.6))'
              }} />
              <span style={{
                fontSize: window.innerWidth <= 480 ? '22px' : '29px'
              }}>
                <span style={{
                  color: '#00ccff', 
                  textShadow: window.innerWidth <= 480 
                    ? '0 0 3px #00ccff, 0 0 6px #00ccff' 
                    : '0 0 5px #00ccff, 0 0 10px #00ccff'
                }}>Inflatable</span>
                <span style={{
                  color: '#ff0080', 
                  textShadow: window.innerWidth <= 480 
                    ? '0 0 3px #ff0080, 0 0 6px #ff0080' 
                    : '0 0 5px #ff0080, 0 0 10px #ff0080'
                }}>TX</span>
              </span>
            </Brand>
            <button 
              onClick={()=>setOpen(false)} 
              aria-label="Close menu"
              style={{
                background: 'none',
                border: 'none',
                color: '#00ccff',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '44px',
                minWidth: '44px'
              }}
            >
              <HiOutlineX size={28} aria-hidden="true" />
            </button>
          </MobileTop>
          <MobileLinks onClick={()=>setOpen(false)}>
            <NavLink to="/" end>Home</NavLink>
            <NavLink to="/products">All Inflatables</NavLink>
            <NavLink to="/bounce-houses">Bounce Houses</NavLink>
            <NavLink to="/water-slides">Water Slides</NavLink>
            <NavLink to="/combos">Combos</NavLink>
            <NavLink to="/obstacle-courses">Obstacle Courses</NavLink>
            <NavLink to="/event-rentals">Event Rentals</NavLink>
            <NavLink to="/booking">Booking</NavLink>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/contact">Contact</NavLink>
            <Link to="/booking"><GlassButton style={{width:'100%', justifyContent:'center'}}>Book Now</GlassButton></Link>
          </MobileLinks>
        </MobileSheet>
      )}
    </HeaderWrap>
  );
}
